import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';

// Raw shape returned by GET /v2/projects/reports/user-performance.
// These types mirror the backend response 1:1; the view-model transform lives in overview.model.ts.

export interface BillableStat {
  billable_minutes: number;
  non_billable_minutes: number;
  total_minutes: number;
  billable_percentage: number;
}

export interface MetricStat {
  current: number;
  previous: number;
  delta: number;
  team_average: number;
}

export interface TimelogsStat {
  current_minutes: number;
  previous_minutes: number;
  delta_minutes: number;
  team_average_minutes: number;
}

export interface ClockAccuracyStat {
  // The backend spec spells this "currenct_percentage"; we also read the
  // corrected "current_percentage" in case the typo is fixed server-side.
  current_percentage?: number;
  currenct_percentage?: number;
  previous_percentage: number;
  delta_percentage: number;
  team_average_percentage: number;
  margin_minutes: number;
}

export interface UserPerformanceData {
  user: { uuid: string; name: string };
  period: { start_date: string; end_date: string; days: number };
  previous_period: { start_date: string; end_date: string };
  billable_hours: {
    current: BillableStat;
    previous: BillableStat;
    team_average: { billable_percentage: number };
  };
  task_overdue: MetricStat;
  request_overdue: MetricStat;
  timelogs: TimelogsStat;
  clock_accuracy: ClockAccuracyStat;
}

export interface UserPerformanceResponse {
  data: UserPerformanceData;
}

export interface FetchUserPerformanceParams {
  /** User UUID to scope the report to. */
  user: string;
  /** Inclusive period start, formatted YYYY-MM-DD. */
  startDate: string;
  /** Inclusive period end, formatted YYYY-MM-DD. */
  endDate: string;
  abort?: AbortController;
}

export const fetchUserPerformance = async ({
  user,
  startDate,
  endDate,
  abort,
}: FetchUserPerformanceParams): Promise<UserPerformanceData> => {
  const res = await apiRequests.get(
    apiRoutes.USER_PERFORMANCE,
    { user, start_date: startDate, end_date: endDate },
    {},
    abort,
  );

  return res.data?.data;
};

export default fetchUserPerformance;
